import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Student, AttendanceRecord } from '../types';
import { STUDENTS } from '../data/students';
import { assignRandomTable, updateTableOccupancy, resetAllTables, updateTableCapacity, saveTableSettings, loadTableSettings, syncTableOccupancy } from '../utils/tableUtils';

interface AttendanceState {
  students: Student[];
  attendanceRecords: AttendanceRecord[];
  selectedStudent: Student | null;
  currentDate: string;
  
  // Actions
  setSelectedStudent: (student: Student | null) => void;
  updateStudentParking: (studentId: string, parkingNumber: string, hasParkingSpace: boolean) => void;
  assignTableToStudent: (studentId: string) => number | null;
  recordAttendance: (studentId: string, parkingNumber?: string, hasParkingSpace?: boolean) => void;
  removeAttendance: (studentId: string) => void;
  toggleParkingRegistration: (studentId: string) => void; // 주차 등록 토글
  resetTables: () => void;
  updateTableCapacity: (tableNumber: number, newCapacity: number) => void;
  saveTableSettings: () => void;
  loadTableSettings: () => void;
  getAttendanceByDate: (date: string) => AttendanceRecord[];
  getStudentAttendance: (studentId: string) => AttendanceRecord | null;
}

export const useAttendanceStore = create<AttendanceState>()(
  persist(
    (set, get) => ({
      students: STUDENTS,
      attendanceRecords: [],
      selectedStudent: null,
      currentDate: new Date().toISOString().split('T')[0],
      
      setSelectedStudent: (student) => {
        set({ selectedStudent: student });
      },
      
      updateStudentParking: (studentId, parkingNumber, hasParkingSpace) => {
        set((state) => ({
          students: state.students.map(student =>
            student.id === studentId
              ? { ...student, parkingNumber, hasParkingSpace }
              : student
          )
        }));
      },
      
      assignTableToStudent: (studentId) => {
        try {
          const tableNumber = assignRandomTable();
          // 점유율 업데이트는 호출하는 쪽에서 처리하도록 제거
          
          set((state) => ({
            students: state.students.map(student =>
              student.id === studentId
                ? { ...student, assignedTable: tableNumber }
                : student
            )
          }));
          
          return tableNumber;
        } catch (error) {
          console.error('테이블 배정 실패:', error);
          return null;
        }
      },
      
      recordAttendance: (studentId, parkingNumber = '', hasParkingSpace = false) => {
        const student = get().students.find(s => s.id === studentId);
        if (!student) return;
        
        const record: AttendanceRecord = {
          studentId,
          studentName: student.name,
          parkingNumber,
          hasParkingSpace,
          date: get().currentDate,
          assignedTable: student.assignedTable,
          isParkingRegistered: false, // 기본값은 미등록
        };
        
        set((state) => {
          const newAttendanceRecords = [
            ...state.attendanceRecords.filter(r => 
              !(r.studentId === studentId && r.date === get().currentDate)
            ),
            record
          ];
          
          // 테이블 점유율 동기화
          syncTableOccupancy(newAttendanceRecords);
          
          return {
            attendanceRecords: newAttendanceRecords
          };
        });
      },
      
      removeAttendance: (studentId) => {
        set((state) => {
          const newAttendanceRecords = state.attendanceRecords.filter(r => 
            !(r.studentId === studentId && r.date === get().currentDate)
          );
          
          // 테이블 점유율 동기화
          syncTableOccupancy(newAttendanceRecords);
          
          return {
            attendanceRecords: newAttendanceRecords
          };
        });
      },
      
      toggleParkingRegistration: (studentId) => {
        set((state) => ({
          attendanceRecords: state.attendanceRecords.map(record => 
            record.studentId === studentId && record.date === get().currentDate
              ? { ...record, isParkingRegistered: !record.isParkingRegistered }
              : record
          )
        }));
      },
      
      resetTables: () => {
        resetAllTables(); // 테이블 점유율 초기화
        set((state) => ({
          students: state.students.map(student => ({
            ...student,
            assignedTable: undefined
          })),
          attendanceRecords: []
        }));
        // 빈 출석 기록으로 동기화
        syncTableOccupancy([]);
      },
      
      updateTableCapacity: (tableNumber, newCapacity) => {
        updateTableCapacity(tableNumber, newCapacity);
        saveTableSettings();
      },
      
      saveTableSettings: () => {
        saveTableSettings();
      },
      
      loadTableSettings: () => {
        loadTableSettings();
      },
      
      getAttendanceByDate: (date) => {
        return get().attendanceRecords
          .filter(record => record.date === date)
          .map(record => ({
            ...record,
            isParkingRegistered: record.isParkingRegistered ?? false // 기존 데이터 호환성
          }));
      },
      
      getStudentAttendance: (studentId) => {
        const record = get().attendanceRecords.find(record => 
          record.studentId === studentId && record.date === get().currentDate
        );
        return record ? {
          ...record,
          isParkingRegistered: record.isParkingRegistered ?? false // 기존 데이터 호환성
        } : null;
      },
    }),
    {
      name: 'attendance-storage',
      partialize: (state) => ({
        students: state.students,
        attendanceRecords: state.attendanceRecords,
        currentDate: state.currentDate,
      }),
    }
  )
); 